import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CapsuleUnitMeasure, Category, Sku, DateRange } from '@emi/shared/models';
import { SkuCapsuleService } from 'libs/shared/data-access/src/lib/sku-capsule/sku-capsule.service';
import { SkuScorecardService } from 'libs/shared/data-access/src/lib/sku-scorecard/sku-scorecard.service';
import { Subscription } from 'rxjs';
import { CapsuleItem, ScorecardDto, ScorecardItem } from './models';
import { environment } from '../../../../../../apps/via-shell/src/environments/environment';
import * as Constants from '@emi/shared/constants';
import { SkuMetadataService } from '@emi/shared/data-access';

@Component({
    selector: 'via-sku-summary',
    templateUrl: './sku-summary.component.html',
    styleUrls: ['./sku-summary.component.scss'],
})
export class SkuSummaryComponent implements OnChanges, OnDestroy {
    @Input()
    public sku: Sku | any;

    @Input()
    public dateRange: DateRange;

    public isLoading: boolean;
    public isPageLoading: boolean;
    public errorResponse: string | undefined;
    public noImagePlaceholderUrl = Constants.GlobalVariables.PLACEHOLDER_200x200;
    public scorecardItems: Array<ScorecardItem> = [];
    public blobStorageUrl: string = environment.imagesUrl + Constants.GlobalVariables.SKU_IMAGES_ORIGINAL;
    public selectedCurrency = 'USD'; //TODO: get selected currency code from state
    public capsuleItem: CapsuleItem;
    public imageSasParams: string;

    public capsuleAttributes: string;
    public capsuleAttributesGroup: Map<string, string>;

    private scorecardSubscription: Subscription = new Subscription();
    private readonly firstChildFromArray: number = 0;
    private readonly categoryLevelOne: number = 1;
    private routerUrlEmiItemId: number;

    constructor(private scorecardService: SkuScorecardService, 
        private capsuleService: SkuCapsuleService, 
        private router: Router,
        private skuMetadataService: SkuMetadataService) { }

    public ngOnInit(): void {
        this.isPageLoading = true;
        this.routerUrlEmiItemId = parseInt(this.router.url.split('id=')[1]);

        this.skuMetadataService.getImageSas().subscribe((sas) => {
            if (sas) {
                this.imageSasParams = '?' + sas;
            } else {
                this.imageSasParams = '';
            }
        });

        this.capsuleService
            .getSkuCapsule(this.routerUrlEmiItemId,)
            .subscribe({
                next: capsule => {
                    this.capsuleItem = capsule as CapsuleItem;
                    this.getAllAttributesKeyValues();
                },
                error: error => {
                    this.isPageLoading = false;
                    this.errorResponse = `${error.error.status} ${error.error.title}`;
                },
                complete: () => {
                    this.isPageLoading = false;
                    this.errorResponse = undefined;
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.sku) {
            this.isLoading = true;

            this.scorecardSubscription = this.scorecardService
                .getSkuScorecard(this.sku.emiItemId, changes.dateRange.currentValue.startDate, changes.dateRange.currentValue.endDate)
                .subscribe({
                    next: scorecard => {
                        this.scorecardItems = this.buildScorecard(scorecard);
                    },
                    error: error => {
                        this.isLoading = false;
                        this.errorResponse = `${error.error.status} ${error.error.title}`;
                    },
                    complete: () => {
                        this.isLoading = false;
                        this.errorResponse = undefined;
                    }
                });
        }
    }

    private buildScorecard(scorecard: ScorecardDto | null): Array<ScorecardItem> {
        return [
            new ScorecardItem('Median Price', scorecard ? this.priceToString(scorecard.price.median) : 'N/A', 'far fa-dollar-sign'),
            new ScorecardItem('Promotional activities', scorecard ? this.valueToString(scorecard.promotionalActivity.daysOnPromotion, 'day'): 'N/A', 'far fa-shopping-cart'),
            new ScorecardItem('Minimum Price', scorecard ? this.priceToString(scorecard.price.minimum) : 'N/A', 'far fa-dollar-sign down-arrow'),
            new ScorecardItem('Maximum Price', scorecard ? this.priceToString(scorecard.price.maximum) : 'N/A', 'far fa-dollar-sign up-arrow'),
            new ScorecardItem('In stock', scorecard ? this.valueToString(scorecard.availability.daysInStock, 'day') : 'N/A', 'far fa-sort-numeric-up-alt'),
            new ScorecardItem('Out of stock', scorecard ? this.valueToString(scorecard.availability.daysOutOfStock, 'day') : 'N/A', 'far fa-sort-numeric-down-alt'),
            new ScorecardItem('Store availability', scorecard ? this.valueToString(scorecard.availability.numberOfRetailers, 'store') : 'N/A', 'far fa-store'),
            new ScorecardItem('Average rating', scorecard ? this.ratingToString(scorecard.rating.average) : 'N/A', 'far fa-star'),
        ];
    }

    private priceToString(price: number | undefined): string {
        return price ? `${price.toFixed(2).toString()} ${this.selectedCurrency}` : 'N/A';
    }

    private valueToString(value: number, type: string): string {
        return value === 1 ? `1 ${type}` : `${value} ${type}s`;
    }

    private ratingToString(rating: number | undefined): string {
        if (!rating) {
            return 'N/A';
        }
        if (rating === 1) {
            return '1 star';
        } else {
            return rating % 1 !== 0 ? `${rating.toFixed(1)} stars` : `${rating} stars`;
        }
    }

    public handleMissingImage(id: number) {
        document.getElementById(id.toString())?.setAttribute('src', this.noImagePlaceholderUrl);
    }

    private getAllAttributesKeyValues(): void {
        if (this.capsuleItem?.attributeGroups?.length) {
            this.capsuleAttributesGroup = new Map();
            this.capsuleItem.attributeGroups.sort((a, b) => (a.name > b.name ? 1 : -1));
            this.capsuleItem.attributeGroups.forEach((attributeElementType) => {
                const keyName = attributeElementType.name;
                let values = '';
                attributeElementType.attributes.sort((a, b) => (a.value > b.value ? 1 : -1));
                attributeElementType.attributes.forEach((attributeKeyValue,index) => {
                    const length = attributeElementType.attributes.length
                    values += length>1 && index !== length-1 ? attributeKeyValue.value + ', ' : attributeKeyValue.value ;
                });
                this.capsuleAttributesGroup.set(keyName,values)
            });
        }

    }

    private removeLastCharacter(string: string, characterToRemove: string): string {
        return string.replace(new RegExp(`${characterToRemove}\\s*$`), '');
    }

    public getLevelOneCategoryDetails(): Category | null {
        let category: Category | null;
        if (this.capsuleItem) {
            const categoryDetails = this.capsuleItem.category?.childCategories?.filter((item) => item?.level === this.categoryLevelOne)?.[
                this.firstChildFromArray
            ];
            category = categoryDetails ?? null;
        } else {
            category = null;
        }
        return category;
    }

    public getSubCategory(): Category | null {
        // Skips level 0 (industry) and level 1 (top category) and finds terminal node category.
        const childCategories = this.capsuleItem.category.childCategories;
        if (childCategories && childCategories[0].childCategories) {
            return this.getLowestLevelCategory(childCategories[0].childCategories[0]);
        }
        return null;
    }

    private getLowestLevelCategory(category: Category): Category {
        if (!category.childCategories) {
            return category;
        }
        return this.getLowestLevelCategory(category.childCategories[0]);
    }

    public getMeasureUnitName(measure: CapsuleUnitMeasure): string | null {
        switch (measure.unitName) {
            case "unknown":
            case "units":
                return measure.unitName = '';
            default:
                return measure.unitName;
        }
    }

    public getDisplayMeasureName(measure: CapsuleUnitMeasure): string | null {
        switch (measure.measureName) {
            case 'Weight':
                return 'Unit Weight';
            case 'Volume':
                return 'Unit Volume';
            default:
                return measure.measureName;
        }
    }

    public ngOnDestroy(): void {
        this.scorecardSubscription.unsubscribe();
    }
}
